import React from 'react'
import {
  catalogIntegrationErrorKey,
  isCatalogIntegrationActive,
  type CatalogIntegrationJob,
} from '@united-square/catalog-contract'
import { Button } from '@runtime/components/ui/Button'
import { useAppStore } from '@runtime/core/store/appStore'
import { useI18n } from '@runtime/core/i18n/i18n'
import {
  CATALOG_INTEGRATIONS_CHANGED,
  listQrIntegrations,
  retryQrIntegration,
} from './catalogIntegrationApi'
import { CATALOG_REMOTE_CHANGED, useCatalogIntegrationStore } from './catalogIntegrationStore'
import styles from './CatalogIntegrationsPanel.module.scss'

export default function CatalogIntegrationsPanel() {
  const token = useAppStore(state => state.authToken)
  const { t } = useI18n()
  const { jobs, setJobs } = useCatalogIntegrationStore()
  const previous = React.useRef<CatalogIntegrationJob[]>([])
  const [error, setError] = React.useState(false)
  const [available, setAvailable] = React.useState(true)
  const [retrying, setRetrying] = React.useState<string | null>(null)
  const [expanded, setExpanded] = React.useState(false)
  React.useEffect(() => {
    if (!token) {
      setJobs([])
      return
    }
    setJobs([])
    previous.current = []
    setError(false)
    const controller = new AbortController()
    let timer: ReturnType<typeof setTimeout> | undefined,
      loading = false
    let refreshRequested = false
    const load = async () => {
      if (loading || controller.signal.aborted) return
      loading = true
      if (timer) clearTimeout(timer)
      let active = true
      try {
        const result = await listQrIntegrations(token, controller.signal)
        if (!controller.signal.aborted) {
          if (
            result.jobs.some(
              job =>
                (isCatalogIntegrationActive(job) ||
                  job.status === 'uncertain' ||
                  job.status === 'failed') &&
                !previous.current.some(old => old.id === job.id),
            )
          )
            setExpanded(true)
          if (
            result.jobs.some(
              job =>
                !isCatalogIntegrationActive(job) &&
                previous.current.some(old => old.id === job.id && isCatalogIntegrationActive(old)),
            )
          )
            window.dispatchEvent(new Event(CATALOG_REMOTE_CHANGED))
          previous.current = result.jobs
          setJobs(result.jobs)
          setAvailable(result.worker.available)
          setError(false)
        }
        active = result.jobs.some(isCatalogIntegrationActive)
      } catch {
        if (!controller.signal.aborted) {
          setError(true)
          setExpanded(true)
        }
      } finally {
        loading = false
        if (!controller.signal.aborted)
          timer = setTimeout(() => void load(), refreshRequested ? 0 : active ? 2000 : 15000)
        refreshRequested = false
      }
    }
    const refresh = () => {
      if (loading) {
        refreshRequested = true
        return
      }
      void load()
    }
    window.addEventListener(CATALOG_INTEGRATIONS_CHANGED, refresh)
    void load()
    return () => {
      controller.abort()
      if (timer) clearTimeout(timer)
      window.removeEventListener(CATALOG_INTEGRATIONS_CHANGED, refresh)
    }
  }, [token, setJobs])
  if (!token || (!jobs.length && !error)) return null
  return (
    <section
      className={styles.panel}
      aria-label={t('hub.admin.integrationJobs.title')}
      data-testid="catalog-integrations-panel"
    >
      <details open={expanded} onToggle={event => setExpanded(event.currentTarget.open)}>
        <summary>{t('hub.admin.integrationJobs.title')}</summary>
        {error ? <p role="alert">{t('hub.admin.integrationJobs.connectionError')}</p> : null}
        {!available ? (
          <p role="status">{t('hub.admin.integrationJobs.workerUnavailable')}</p>
        ) : null}
        <ul>
          {jobs.map(job => (
            <li key={job.id} data-testid={`catalog-integration-${job.id}`}>
              <p role="status">
                {t(`hub.admin.integrationJobs.states.${job.status}`)} ·{' '}
                {t(`hub.admin.integrationJobs.stages.${job.stage}`)} ·{' '}
                {t('hub.admin.integrationJobs.attempt', { count: job.attempt })}
              </p>
              <p>{job.label}</p>
              {job.errorCode ? (
                <p>
                  {t(
                    `hub.admin.integrationJobs.errors.${catalogIntegrationErrorKey(job.errorCode)}`,
                    { defaultValue: t('hub.admin.integrationJobs.errors.generic') },
                  )}
                </p>
              ) : null}
              {job.status === 'uncertain' ? (
                <p>{t('hub.admin.integrationJobs.uncertainHelp')}</p>
              ) : null}
              {job.retryable || job.status === 'uncertain' ? (
                <Button
                  size="s"
                  disabled={retrying === job.id}
                  onClick={() => {
                    setRetrying(job.id)
                    void retryQrIntegration(token, job.id)
                      .catch(() => setError(true))
                      .finally(() => setRetrying(null))
                  }}
                >
                  {t(
                    job.status === 'uncertain'
                      ? 'hub.admin.integrationJobs.reconcile'
                      : 'hub.admin.integrationJobs.retry',
                  )}
                </Button>
              ) : null}
            </li>
          ))}
        </ul>
      </details>
    </section>
  )
}
